/**
 * Surface geometry and view projections.
 *
 * The artwork lives on the unwrapped print surface (u along the circumference,
 * v down from the top of the print band, both in mm). A Projection maps that
 * surface to stage coordinates (also mm, used as the SVG viewBox) and back.
 *
 * - CylinderProjection: orthographic front view of the container seen slightly
 *   from above, rotatable through 360° (viewAngle).
 * - FlatProjection: the unwrapped band, 1:1.
 *
 * A WebGL/3D preview can be added as another renderer on the same surface
 * texture without touching placement, editing or RFQ code.
 */
import type { ArtworkPlacement } from '@shared/rfq';
import { circumference, maxRadius, type ContainerModel } from './model';

export interface Point {
  x: number;
  y: number;
}
export interface SurfacePoint {
  u: number;
  v: number;
}
export interface ViewBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface Projection {
  kind: 'cylinder' | 'flat';
  viewBox: ViewBox;
  circumferenceMm: number;
  bandHeightMm: number;
  /** Surface → stage; `visible` is false on the back side of the cylinder. */
  toStage(p: SurfacePoint): Point & { visible: boolean; facing: number };
  /** Stage → surface; null outside the cylinder silhouette. */
  toSurface(p: Point): SurfacePoint | null;
  /** u at the centre of the view. */
  frontU: number;
}

/** Ellipse ratio for horizontal circles (camera slightly above the jar). */
export const ELLIPSE_RATIO = 0.16;

export function wrapU(u: number, c: number): number {
  return ((u % c) + c) % c;
}
/** Signed shortest distance a→b on a circle of length c. */
export function deltaU(a: number, b: number, c: number): number {
  let d = wrapU(b - a, c);
  if (d > c / 2) d -= c;
  return d;
}

export function containerViewBox(c: ContainerModel): ViewBox {
  const r = maxRadius(c);
  const top = -(c.closure ? c.closure.heightMm + 8 : 20);
  const bottom = c.heightMm + r * ELLIPSE_RATIO + 14;
  const pad = 16 + (c.handle ? c.handle.widthMm + 6 : 0) + (c.closure?.kind === 'reeds' ? r * 0.6 : 0) + (c.closure?.kind === 'pump' ? 10 : 0);
  return { x: -r - pad, y: top, w: 2 * (r + pad), h: bottom - top };
}

export function cylinderProjection(c: ContainerModel, viewAngleDeg: number): Projection {
  const r = c.diameterMm / 2;
  const C = circumference(c);
  const e = ELLIPSE_RATIO;
  const bandTop = c.print.topMm;
  const phi = (viewAngleDeg * Math.PI) / 180;
  const frontU = wrapU(phi * r, C);
  return {
    kind: 'cylinder',
    viewBox: containerViewBox(c),
    circumferenceMm: C,
    bandHeightMm: c.print.heightMm,
    frontU,
    toStage({ u, v }) {
      const theta = u / r - phi;
      const cos = Math.cos(theta);
      return { x: r * Math.sin(theta), y: bandTop + v + e * r * cos, visible: cos > 0, facing: cos };
    },
    toSurface({ x, y }) {
      if (Math.abs(x) >= r) return null;
      const theta = Math.asin(x / r);
      const v = y - bandTop - e * r * Math.cos(theta);
      return { u: wrapU((theta + phi) * r, C), v };
    },
  };
}

export const FLAT_MARGIN = { x: 14, top: 14, bottom: 16 };

export function flatProjection(c: ContainerModel, frontU: number): Projection {
  const C = circumference(c);
  const H = c.print.heightMm;
  return {
    kind: 'flat',
    viewBox: { x: -FLAT_MARGIN.x, y: -FLAT_MARGIN.top, w: C + 2 * FLAT_MARGIN.x, h: H + FLAT_MARGIN.top + FLAT_MARGIN.bottom },
    circumferenceMm: C,
    bandHeightMm: H,
    frontU,
    toStage({ u, v }) {
      return { x: u, y: v, visible: true, facing: 1 };
    },
    toSurface({ x, y }) {
      return { u: x, v: y };
    },
  };
}

/** Corners of the rotated artwork box on the surface, clockwise from top-left. */
export function placementCorners(p: ArtworkPlacement): SurfacePoint[] {
  const a = (p.rotationDeg * Math.PI) / 180;
  const cos = Math.cos(a);
  const sin = Math.sin(a);
  const hw = p.widthMm / 2;
  const hh = p.heightMm / 2;
  return [
    [-hw, -hh],
    [hw, -hh],
    [hw, hh],
    [-hw, hh],
  ].map(([dx, dy]) => ({ u: p.uMm + dx * cos - dy * sin, v: p.vMm + dx * sin + dy * cos }));
}

/** Local offset (in the artwork's own axes) → surface point. */
export function localToSurface(p: ArtworkPlacement, dx: number, dy: number): SurfacePoint {
  const a = (p.rotationDeg * Math.PI) / 180;
  return { u: p.uMm + dx * Math.cos(a) - dy * Math.sin(a), v: p.vMm + dx * Math.sin(a) + dy * Math.cos(a) };
}

export function boundingBox(p: ArtworkPlacement): { w: number; h: number } {
  const a = (p.rotationDeg * Math.PI) / 180;
  const c = Math.abs(Math.cos(a));
  const s = Math.abs(Math.sin(a));
  return { w: p.widthMm * c + p.heightMm * s, h: p.widthMm * s + p.heightMm * c };
}

/** Is a surface point inside the rotated artwork (wrap-aware)? */
export function hitsArtwork(p: ArtworkPlacement, pt: SurfacePoint, C: number, slopMm = 0): boolean {
  const du = deltaU(p.uMm, pt.u, C);
  const dv = pt.v - p.vMm;
  const a = (-p.rotationDeg * Math.PI) / 180;
  const lx = du * Math.cos(a) - dv * Math.sin(a);
  const ly = du * Math.sin(a) + dv * Math.cos(a);
  return Math.abs(lx) <= p.widthMm / 2 + slopMm && Math.abs(ly) <= p.heightMm / 2 + slopMm;
}

export interface PlacementReport {
  coverageRatio: number;
  outsideBand: boolean;
  wrapFraction: number;
  /** Artwork overlaps a no-print zone (e.g. mug handle). */
  hitsNoPrint: boolean;
}

export function analysePlacement(p: ArtworkPlacement, C: number, H: number, noPrint: { uMm: number; widthMm: number }[] = []): PlacementReport {
  const bb = boundingBox(p);
  const hitsNoPrint = noPrint.some((z) => Math.abs(deltaU(p.uMm, z.uMm, C)) < bb.w / 2 + z.widthMm / 2);
  const top = p.vMm - bb.h / 2;
  const bottom = p.vMm + bb.h / 2;
  const visibleH = Math.max(0, Math.min(bottom, H) - Math.max(top, 0));
  const visibleW = Math.min(bb.w, C);
  return {
    coverageRatio: Math.min(1, (visibleW * visibleH) / (C * H)),
    outsideBand: top < -0.5 || bottom > H + 0.5,
    wrapFraction: Math.min(1, bb.w / C),
    hitsNoPrint,
  };
}

/** Initial placement: centred on the visible front, fitting comfortably. */
export function defaultPlacement(c: ContainerModel, aspect: number, frontU: number): ArtworkPlacement {
  const H = c.print.heightMm;
  const maxW = c.diameterMm * 0.72;
  const maxH = H * 0.72;
  let w = maxW;
  let h = w / aspect;
  if (h > maxH) {
    h = maxH;
    w = h * aspect;
  }
  return { uMm: frontU, vMm: H / 2, widthMm: round1(w), heightMm: round1(h), rotationDeg: 0 };
}

export function fullWrapPlacement(c: ContainerModel, aspect: number, frontU: number): ArtworkPlacement {
  const C = circumference(c);
  const H = c.print.heightMm;
  // Cover the whole band; height follows the aspect ratio (overflow is clipped by the band).
  return { uMm: frontU, vMm: H / 2, widthMm: round1(C), heightMm: round1(C / aspect), rotationDeg: 0 };
}

export function suggestCoverage(r: PlacementReport, bb: { w: number; h: number }, C: number, H: number) {
  if (r.wrapFraction >= 0.85) return 'full_wrap' as const;
  if (bb.w <= C * 0.22 && bb.h <= H * 0.7) return 'single_logo' as const;
  return 'partial' as const;
}

export function round1(n: number): number {
  return Math.round(n * 10) / 10;
}
